import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { useAuth } from '../../context/AuthContext';
import { useEstablishment } from '../../context/EstablishmentContext';
import { 
  Building2, 
  Plus, 
  Users, 
  Settings, 
  MapPin, 
  Phone, 
  Mail,
  Trash2,
  Edit,
  UserPlus,
  Crown,
  Shield,
  User,
  Eye,
  EyeOff,
  Check,
  X,
  AlertCircle,
  Copy,
  UtensilsCrossed
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { getApiErrorMessage } from '../../utils/errorHelper';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MultiSiteManagement() {
  const { isAuthenticated } = useAuth();
  const { establishments, hasGroup, refreshEstablishments, canManageEstablishments } = useEstablishment();
  
  const [activeTab, setActiveTab] = useState('establishments');
  const [loading, setLoading] = useState(true);
  const [group, setGroup] = useState(null);
  const [subAccounts, setSubAccounts] = useState([]);
  
  // Modals
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [showAddEstablishment, setShowAddEstablishment] = useState(false);
  const [showAddSubAccount, setShowAddSubAccount] = useState(false);
  const [showEditSubAccount, setShowEditSubAccount] = useState(null);
  const [showCopyMenu, setShowCopyMenu] = useState(null); // target establishment
  
  // Form states
  const [groupForm, setGroupForm] = useState({ name: '', description: '' });
  const [establishmentForm, setEstablishmentForm] = useState({
    name: '', address: '', city: '', phone: '', email: ''
  });
  const [subAccountForm, setSubAccountForm] = useState({
    email: '', password: '', name: '', phone: '', role: 'employee', establishment_ids: []
  });
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copyMenuSource, setCopyMenuSource] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    if (!isAuthenticated) return;
    
    try {
      setLoading(true);
      
      // Fetch group info
      const groupRes = await axios.get(`${API}/multisite/groups/my-group`);
      setGroup(groupRes.data);
      
      // Fetch sub-accounts if has group
      if (groupRes.data.id) {
        const subRes = await axios.get(`${API}/multisite/sub-accounts`);
        setSubAccounts(subRes.data.sub_accounts || []);
      }
    } catch (err) {
      if (err.response?.status !== 404) {
        console.error('Error fetching data:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGroup = async () => {
    if (!groupForm.name.trim()) {
      toast.error('Le nom du groupe est requis');
      return;
    }
    
    try {
      setSaving(true);
      await axios.post(`${API}/multisite/groups`, groupForm);
      
      toast.success('Groupe créé avec succès !');
      setShowCreateGroup(false);
      setGroupForm({ name: '', description: '' });
      fetchData();
      refreshEstablishments();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Erreur lors de la création'));
    } finally {
      setSaving(false);
    }
  };

  const handleAddEstablishment = async () => {
    if (!establishmentForm.name.trim() || !establishmentForm.address.trim() || 
        !establishmentForm.city.trim() || !establishmentForm.phone.trim()) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    try {
      setSaving(true);
      await axios.post(`${API}/multisite/establishments`, establishmentForm);
      
      toast.success('Établissement ajouté !');
      setShowAddEstablishment(false);
      setEstablishmentForm({ name: '', address: '', city: '', phone: '', email: '' });
      fetchData();
      refreshEstablishments();
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Erreur lors de l'ajout"));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEstablishment = async (id, name) => {
    if (!window.confirm(`Supprimer l'établissement "${name}" ? Cette action est irréversible.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/multisite/establishments/${id}`);
      
      toast.success('Établissement supprimé');
      fetchData();
      refreshEstablishments();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Erreur lors de la suppression'));
    }
  };

  const handleAddSubAccount = async () => {
    if (!subAccountForm.email || !subAccountForm.password || !subAccountForm.name) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    if (subAccountForm.establishment_ids.length === 0) {
      toast.error('Sélectionnez au moins un établissement');
      return;
    }
    
    try {
      setSaving(true);
      await axios.post(`${API}/multisite/sub-accounts`, subAccountForm);
      
      toast.success('Sous-compte créé !');
      setShowAddSubAccount(false);
      setSubAccountForm({
        email: '', password: '', name: '', phone: '', role: 'employee', establishment_ids: []
      });
      fetchData();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Erreur lors de la création'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSubAccount = async (id, name) => {
    if (!window.confirm(`Supprimer le compte de "${name}" ?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/multisite/sub-accounts/${id}`);
      
      toast.success('Sous-compte supprimé');
      fetchData();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Erreur lors de la suppression'));
    }
  };

  const handleCopyMenu = async () => {
    if (!copyMenuSource || !showCopyMenu) {
      toast.error('Sélectionnez un établissement source');
      return;
    }
    
    try {
      setSaving(true);
      const response = await axios.post(
        `${API}/multisite/establishments/${showCopyMenu.id}/copy-menu/${copyMenuSource}`,
        {}
      );
      
      toast.success(`Menu copié ! ${response.data.categories_created} catégories et ${response.data.dishes_created} plats créés`);
      setShowCopyMenu(null);
      setCopyMenuSource('');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Erreur lors de la copie'));
    } finally {
      setSaving(false);
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'owner': return <Crown className="w-4 h-4 text-amber-500" />;
      case 'manager': return <Shield className="w-4 h-4 text-blue-500" />;
      default: return <User className="w-4 h-4 text-gray-500" />;
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'owner': return 'Propriétaire';
      case 'manager': return 'Manager';
      default: return 'Employé';
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="multisite-management">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Building2 className="w-7 h-7 text-emerald-500" />
              Multi-établissements
            </h1>
            <p className="text-gray-500 mt-1">
              Gérez vos différents points de vente et les accès de votre équipe
            </p>
          </div>
          
          {!hasGroup && (
            <Button
              onClick={() => setShowCreateGroup(true)}
              className="bg-emerald-500 hover:bg-emerald-600"
              data-testid="create-group-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Créer un groupe
            </Button>
          )}
        </div>

        {/* Group Info Card */}
        {group && group.id && (
          <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-2xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold">{group.name}</h2>
                {group.description && (
                  <p className="text-emerald-100 mt-1">{group.description}</p>
                )}
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold">{group.establishments_count}</p>
                <p className="text-emerald-100 text-sm">établissement{group.establishments_count > 1 ? 's' : ''}</p>
              </div>
            </div>
          </div>
        )}

        {/* Not a group yet message */}
        {!hasGroup && (
          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-blue-900">Mode établissement unique</h3>
                <p className="text-blue-700 mt-1">
                  Vous n'avez pas encore créé de groupe multi-établissements. 
                  Créez un groupe pour ajouter d'autres points de vente et gérer des sous-comptes pour votre équipe.
                </p>
                <Button
                  onClick={() => setShowCreateGroup(true)}
                  variant="outline"
                  className="mt-4 border-blue-300 text-blue-700 hover:bg-blue-100"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Créer mon groupe
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        {hasGroup && (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid grid-cols-2 w-full max-w-md">
              <TabsTrigger value="establishments" className="flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                Établissements
              </TabsTrigger>
              <TabsTrigger value="team" className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                Équipe
              </TabsTrigger>
            </TabsList>

            {/* Establishments Tab */}
            <TabsContent value="establishments" className="mt-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  Mes établissements ({establishments.length})
                </h2>
                <Button
                  onClick={() => setShowAddEstablishment(true)}
                  className="bg-emerald-500 hover:bg-emerald-600"
                  data-testid="add-establishment-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter
                </Button>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                {establishments.map((est) => (
                  <div
                    key={est.id}
                    className="bg-white rounded-2xl border border-gray-100 p-5 hover:shadow-md transition-shadow"
                    data-testid={`establishment-card-${est.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                          <Building2 className="w-6 h-6 text-emerald-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                            {est.name}
                            {est.is_main && (
                              <span className="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full">
                                Principal
                              </span>
                            )}
                          </h3>
                          <div className="mt-2 space-y-1 text-sm text-gray-500">
                            <p className="flex items-center gap-1.5">
                              <MapPin className="w-3.5 h-3.5" />
                              {est.address}, {est.city}
                            </p>
                            {est.phone && (
                              <p className="flex items-center gap-1.5">
                                <Phone className="w-3.5 h-3.5" />
                                {est.phone}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {!est.is_main && (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => {
                              setShowCopyMenu(est);
                              setCopyMenuSource('');
                            }}
                            className="p-2 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                            title="Copier un menu depuis un autre établissement"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteEstablishment(est.id, est.name)}
                            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                    
                    <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        est.is_active 
                          ? 'bg-emerald-100 text-emerald-700' 
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {est.is_active ? 'Actif' : 'Inactif'}
                      </span>
                      {!est.is_main && (
                        <button
                          onClick={() => {
                            setShowCopyMenu(est);
                            setCopyMenuSource('');
                          }}
                          className="text-xs text-emerald-600 hover:text-emerald-700 flex items-center gap-1"
                        >
                          <UtensilsCrossed className="w-3 h-3" />
                          Copier un menu
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* Team Tab */}
            <TabsContent value="team" className="mt-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  Mon équipe ({subAccounts.length} membre{subAccounts.length > 1 ? 's' : ''})
                </h2>
                <Button
                  onClick={() => setShowAddSubAccount(true)}
                  className="bg-emerald-500 hover:bg-emerald-600"
                  data-testid="add-subaccount-btn"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Ajouter un membre
                </Button>
              </div>

              {subAccounts.length === 0 ? (
                <div className="bg-gray-50 rounded-2xl p-8 text-center">
                  <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="font-semibold text-gray-900">Aucun membre dans l'équipe</h3>
                  <p className="text-gray-500 mt-1">
                    Ajoutez des sous-comptes pour permettre à votre équipe d'accéder au dashboard
                  </p>
                </div>
              ) : (
                <div className="bg-white rounded-2xl border border-gray-100 divide-y divide-gray-100">
                  {subAccounts.map((account) => (
                    <div
                      key={account.id}
                      className="p-4 flex items-center justify-between"
                      data-testid={`subaccount-${account.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                          <span className="text-sm font-medium text-gray-600">
                            {account.name?.charAt(0)?.toUpperCase() || '?'}
                          </span>
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-gray-900">{account.name}</p>
                            {getRoleIcon(account.role)}
                            <span className="text-xs text-gray-500">
                              {getRoleLabel(account.role)}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500">{account.email}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-xs text-gray-500">Établissements</p>
                          <p className="text-sm font-medium text-gray-700">
                            {account.establishment_names?.join(', ') || '-'}
                          </p>
                        </div>
                        
                        <button
                          onClick={() => handleDeleteSubAccount(account.id, account.name)}
                          className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}

        {/* Create Group Modal */}
        <Dialog open={showCreateGroup} onOpenChange={setShowCreateGroup}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Créer un groupe multi-établissements</DialogTitle>
              <DialogDescription>
                Créez un groupe pour gérer plusieurs points de vente depuis un seul compte.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <Label>Nom du groupe *</Label>
                <Input
                  value={groupForm.name}
                  onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })}
                  placeholder="Ex: Ma Chaîne de Restaurants"
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Input
                  value={groupForm.description}
                  onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })}
                  placeholder="Description optionnelle"
                  className="mt-1.5"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateGroup(false)}>
                Annuler
              </Button>
              <Button
                onClick={handleCreateGroup}
                disabled={saving}
                className="bg-emerald-500 hover:bg-emerald-600"
              >
                {saving ? 'Création...' : 'Créer le groupe'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Establishment Modal */}
        <Dialog open={showAddEstablishment} onOpenChange={setShowAddEstablishment}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Ajouter un établissement</DialogTitle>
              <DialogDescription>
                Ajoutez un nouveau point de vente à votre groupe.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <Label>Nom de l'établissement *</Label>
                <Input
                  value={establishmentForm.name}
                  onChange={(e) => setEstablishmentForm({ ...establishmentForm, name: e.target.value })}
                  placeholder="Ex: Mon Restaurant - Centre-ville"
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Adresse *</Label>
                <Input
                  value={establishmentForm.address}
                  onChange={(e) => setEstablishmentForm({ ...establishmentForm, address: e.target.value })}
                  placeholder="123 Rue Example"
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Ville *</Label>
                <Input
                  value={establishmentForm.city}
                  onChange={(e) => setEstablishmentForm({ ...establishmentForm, city: e.target.value })}
                  placeholder="Paris"
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Téléphone *</Label>
                <Input
                  value={establishmentForm.phone}
                  onChange={(e) => setEstablishmentForm({ ...establishmentForm, phone: e.target.value })}
                  placeholder="+33 1 23 45 67 89"
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={establishmentForm.email}
                  onChange={(e) => setEstablishmentForm({ ...establishmentForm, email: e.target.value })}
                  placeholder="contact@example.com"
                  className="mt-1.5"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddEstablishment(false)}>
                Annuler
              </Button>
              <Button
                onClick={handleAddEstablishment}
                disabled={saving}
                className="bg-emerald-500 hover:bg-emerald-600"
              >
                {saving ? 'Ajout...' : 'Ajouter'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Sub-Account Modal */}
        <Dialog open={showAddSubAccount} onOpenChange={setShowAddSubAccount}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Ajouter un membre à l'équipe</DialogTitle>
              <DialogDescription>
                Créez un sous-compte pour un membre de votre équipe.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nom complet *</Label>
                  <Input
                    value={subAccountForm.name}
                    onChange={(e) => setSubAccountForm({ ...subAccountForm, name: e.target.value })}
                    placeholder="Jean Dupont"
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label>Téléphone</Label>
                  <Input
                    value={subAccountForm.phone}
                    onChange={(e) => setSubAccountForm({ ...subAccountForm, phone: e.target.value })}
                    placeholder="+33 6 12 34 56 78"
                    className="mt-1.5"
                  />
                </div>
              </div>
              
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={subAccountForm.email}
                  onChange={(e) => setSubAccountForm({ ...subAccountForm, email: e.target.value })}
                  placeholder="employe@example.com"
                  className="mt-1.5"
                />
              </div>
              
              <div>
                <Label>Mot de passe *</Label>
                <div className="relative mt-1.5">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={subAccountForm.password}
                    onChange={(e) => setSubAccountForm({ ...subAccountForm, password: e.target.value })}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              <div>
                <Label>Rôle *</Label>
                <Select
                  value={subAccountForm.role}
                  onValueChange={(value) => setSubAccountForm({ ...subAccountForm, role: value })}
                >
                  <SelectTrigger className="mt-1.5">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manager">
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4 text-blue-500" />
                        Manager (accès complet)
                      </div>
                    </SelectItem>
                    <SelectItem value="employee">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-gray-500" />
                        Employé (accès limité)
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Établissements accessibles *</Label>
                <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
                  {establishments.map((est) => (
                    <label
                      key={est.id}
                      className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={subAccountForm.establishment_ids.includes(est.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSubAccountForm({
                              ...subAccountForm,
                              establishment_ids: [...subAccountForm.establishment_ids, est.id]
                            });
                          } else {
                            setSubAccountForm({
                              ...subAccountForm,
                              establishment_ids: subAccountForm.establishment_ids.filter(id => id !== est.id)
                            });
                          }
                        }}
                        className="w-4 h-4 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500"
                      />
                      <span className="text-sm text-gray-700">{est.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddSubAccount(false)}>
                Annuler
              </Button>
              <Button
                onClick={handleAddSubAccount}
                disabled={saving}
                className="bg-emerald-500 hover:bg-emerald-600"
              >
                {saving ? 'Création...' : 'Créer le compte'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Copy Menu Modal */}
        <Dialog open={!!showCopyMenu} onOpenChange={(open) => !open && setShowCopyMenu(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Copy className="w-5 h-5 text-emerald-600" />
                Copier un menu
              </DialogTitle>
              <DialogDescription>
                Copiez le menu complet (catégories et plats) d'un autre établissement vers <strong>{showCopyMenu?.name}</strong>.
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4">
              <Label>Copier depuis</Label>
              <Select
                value={copyMenuSource}
                onValueChange={setCopyMenuSource}
              >
                <SelectTrigger className="mt-1.5">
                  <SelectValue placeholder="Sélectionnez un établissement source" />
                </SelectTrigger>
                <SelectContent>
                  {establishments
                    .filter(e => e.id !== showCopyMenu?.id)
                    .map((est) => (
                      <SelectItem key={est.id} value={est.id}>
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-gray-400" />
                          {est.name}
                          {est.is_main && (
                            <span className="text-xs bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded">
                              Principal
                            </span>
                          )}
                        </div>
                      </SelectItem>
                    ))
                  }
                </SelectContent>
              </Select>
              
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  <strong>Attention :</strong> Cette action va créer une copie du menu. Les modifications futures sur l'établissement source ne seront pas synchronisées.
                </p>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCopyMenu(null)}>
                Annuler
              </Button>
              <Button
                onClick={handleCopyMenu}
                disabled={saving || !copyMenuSource}
                className="bg-emerald-500 hover:bg-emerald-600"
              >
                {saving ? 'Copie en cours...' : 'Copier le menu'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
